/**
 * Section copy editor.
 *
 * Covers every narrative block on the site — about, mission, vision, mindset,
 * process, why-AGENZ, creative execution, the marquee, and the footer.
 *
 * Blank fields fall back to the original deck copy rather than publishing an
 * empty string, so someone experimenting can always get back to the starting
 * point by clearing a field.
 */
import { useEffect, useState } from 'react';
import { defaultSections } from '@agenz/shared/sections';
import { api } from '../lib/api.js';
import {
  Button,
  Card,
  ErrorNote,
  Field,
  Input,
  PageTitle,
  SaveState,
  Spinner,
  Textarea,
} from '../components/ui.jsx';

/** Editable list of plain strings (mindset principles, why-points, marquee). */
function StringList({ items = [], onChange, placeholder, rows = 2 }) {
  return (
    <div className="space-y-3">
      {items.map((value, index) => (
        <div key={index} className="flex gap-3">
          {rows > 1 ? (
            <Textarea
              rows={rows}
              value={value}
              placeholder={placeholder}
              onChange={(e) => onChange(items.map((v, i) => (i === index ? e.target.value : v)))}
            />
          ) : (
            <Input
              value={value}
              placeholder={placeholder}
              onChange={(e) => onChange(items.map((v, i) => (i === index ? e.target.value : v)))}
            />
          )}
          <Button
            type="button"
            variant="danger"
            size="sm"
            className="h-fit"
            onClick={() => onChange(items.filter((_, i) => i !== index))}
          >
            ×
          </Button>
        </div>
      ))}
      <Button type="button" variant="secondary" size="sm" onClick={() => onChange([...items, ''])}>
        + Add
      </Button>
    </div>
  );
}

/** The four process steps: number, title, body. */
function StepList({ steps = [], onChange }) {
  const set = (index, key, value) =>
    onChange(steps.map((s, i) => (i === index ? { ...s, [key]: value } : s)));

  return (
    <div className="space-y-5">
      {steps.map((step, index) => (
        <Card key={index} className="p-4">
          <div className="flex gap-3">
            <Input
              className="w-20"
              value={step.number ?? ''}
              placeholder="01"
              onChange={(e) => set(index, 'number', e.target.value)}
            />
            <Input
              value={step.title ?? ''}
              placeholder="Step title"
              onChange={(e) => set(index, 'title', e.target.value)}
            />
            <Button
              type="button"
              variant="danger"
              size="sm"
              className="h-fit"
              onClick={() => onChange(steps.filter((_, i) => i !== index))}
            >
              ×
            </Button>
          </div>
          <Textarea
            className="mt-3"
            rows={2}
            value={step.body ?? ''}
            placeholder="What happens in this step"
            onChange={(e) => set(index, 'body', e.target.value)}
          />
        </Card>
      ))}
      <Button
        type="button"
        variant="secondary"
        size="sm"
        onClick={() =>
          onChange([...steps, { number: String(steps.length + 1).padStart(2, '0'), title: '', body: '' }])
        }
      >
        + Add step
      </Button>
    </div>
  );
}

function Section({ title, description, children }) {
  return (
    <Card className="p-8">
      <h2 className="text-xl">{title}</h2>
      {description && <p className="mt-2 text-sm text-muted">{description}</p>}
      <div className="mt-6 space-y-5">{children}</div>
    </Card>
  );
}

export default function Sections() {
  const [data, setData] = useState(null);
  const [state, setState] = useState('idle');
  const [error, setError] = useState(null);

  function load() {
    setError(null);
    api
      .get('/api/admin/settings')
      .then(({ settings }) => {
        // Show the effective copy (stored over defaults) so the editor always
        // starts from what is actually on the site.
        const defaults = defaultSections();
        const stored = settings?.sections ?? {};
        const merged = {};
        for (const [key, fallback] of Object.entries(defaults)) {
          merged[key] = { ...fallback, ...(stored[key] ?? {}) };
        }
        setData(merged);
      })
      .catch((err) => setError(err.message));
  }

  useEffect(load, []);

  const set = (section, field, value) =>
    setData((prev) => ({ ...prev, [section]: { ...prev[section], [field]: value } }));

  async function save() {
    setState('saving');
    setError(null);
    try {
      await api.patch('/api/admin/settings', { sections: data });
      setState('saved');
      setTimeout(() => setState('idle'), 2500);
    } catch (err) {
      setState('error');
      setError(err.message);
    }
  }

  function reset() {
    setData(defaultSections());
  }

  if (error && !data) return <ErrorNote error={error} onRetry={load} />;
  if (!data) return <Spinner />;

  return (
    <>
      <PageTitle
        title="Sections"
        description="Every block of copy on the site. Changes reach the live pages within a couple of seconds. Clearing a field restores the original wording."
        action={
          <div className="flex items-center gap-4">
            <SaveState state={state} />
            <Button variant="ghost" onClick={reset}>
              Reset to original
            </Button>
            <Button onClick={save} disabled={state === 'saving'}>
              Save sections
            </Button>
          </div>
        }
      />

      <ErrorNote error={error} />

      <div className="grid gap-8 lg:grid-cols-2">
        <div className="space-y-8">
          <Section title="About" description="Home page intro and the About page header.">
            <Field label="Eyebrow" htmlFor="about-eyebrow">
              <Input id="about-eyebrow" value={data.about.eyebrow ?? ''} onChange={(e) => set('about', 'eyebrow', e.target.value)} />
            </Field>
            <Field label="Headline" htmlFor="about-headline">
              <Input id="about-headline" value={data.about.headline ?? ''} onChange={(e) => set('about', 'headline', e.target.value)} />
            </Field>
            <Field label="Headline second line" htmlFor="about-accent" hint="Shown in the accent colour.">
              <Input id="about-accent" value={data.about.headlineAccent ?? ''} onChange={(e) => set('about', 'headlineAccent', e.target.value)} />
            </Field>
            <Field label="Body" htmlFor="about-body">
              <Textarea id="about-body" rows={5} value={data.about.body ?? ''} onChange={(e) => set('about', 'body', e.target.value)} />
            </Field>
          </Section>

          <Section title="Mission">
            <Field label="Eyebrow" htmlFor="mission-eyebrow">
              <Input id="mission-eyebrow" value={data.mission.eyebrow ?? ''} onChange={(e) => set('mission', 'eyebrow', e.target.value)} />
            </Field>
            <Field label="Headline" htmlFor="mission-headline">
              <Input id="mission-headline" value={data.mission.headline ?? ''} onChange={(e) => set('mission', 'headline', e.target.value)} />
            </Field>
            <Field label="Body" htmlFor="mission-body">
              <Textarea id="mission-body" rows={4} value={data.mission.body ?? ''} onChange={(e) => set('mission', 'body', e.target.value)} />
            </Field>
          </Section>

          <Section title="Vision" description="Shown on the About page.">
            <Field label="Eyebrow" htmlFor="vision-eyebrow">
              <Input id="vision-eyebrow" value={data.vision.eyebrow ?? ''} onChange={(e) => set('vision', 'eyebrow', e.target.value)} />
            </Field>
            <Field label="Headline" htmlFor="vision-headline">
              <Textarea id="vision-headline" rows={2} value={data.vision.headline ?? ''} onChange={(e) => set('vision', 'headline', e.target.value)} />
            </Field>
            <Field label="Body" htmlFor="vision-body">
              <Textarea id="vision-body" rows={4} value={data.vision.body ?? ''} onChange={(e) => set('vision', 'body', e.target.value)} />
            </Field>
          </Section>

          <Section title="Mindset" description="The principles list, on the home page and About.">
            <Field label="Eyebrow" htmlFor="mindset-eyebrow">
              <Input id="mindset-eyebrow" value={data.mindset.eyebrow ?? ''} onChange={(e) => set('mindset', 'eyebrow', e.target.value)} />
            </Field>
            <Field label="Headline" htmlFor="mindset-headline">
              <Textarea id="mindset-headline" rows={2} value={data.mindset.headline ?? ''} onChange={(e) => set('mindset', 'headline', e.target.value)} />
            </Field>
            <Field label="Principles">
              <StringList items={data.mindset.principles ?? []} onChange={(v) => set('mindset', 'principles', v)} placeholder="A principle" />
            </Field>
          </Section>
        </div>

        <div className="space-y-8">
          <Section title="Process" description="The numbered steps on the home and Process pages.">
            <Field label="Eyebrow" htmlFor="process-eyebrow">
              <Input id="process-eyebrow" value={data.process.eyebrow ?? ''} onChange={(e) => set('process', 'eyebrow', e.target.value)} />
            </Field>
            <Field label="Steps">
              <StepList steps={data.process.steps ?? []} onChange={(v) => set('process', 'steps', v)} />
            </Field>
          </Section>

          <Section title="Why AGENZ">
            <Field label="Eyebrow" htmlFor="why-eyebrow">
              <Input id="why-eyebrow" value={data.whyAgenz.eyebrow ?? ''} onChange={(e) => set('whyAgenz', 'eyebrow', e.target.value)} />
            </Field>
            <Field label="Headline" htmlFor="why-headline">
              <Input id="why-headline" value={data.whyAgenz.headline ?? ''} onChange={(e) => set('whyAgenz', 'headline', e.target.value)} />
            </Field>
            <Field label="Points">
              <StringList items={data.whyAgenz.points ?? []} onChange={(v) => set('whyAgenz', 'points', v)} placeholder="A reason" />
            </Field>
          </Section>

          <Section title="Creative execution" description="Shown at the foot of the Services page.">
            <Field label="Eyebrow" htmlFor="ce-eyebrow">
              <Input id="ce-eyebrow" value={data.creativeExecution.eyebrow ?? ''} onChange={(e) => set('creativeExecution', 'eyebrow', e.target.value)} />
            </Field>
            <Field label="Body" htmlFor="ce-body">
              <Textarea id="ce-body" rows={6} value={data.creativeExecution.body ?? ''} onChange={(e) => set('creativeExecution', 'body', e.target.value)} />
            </Field>
          </Section>

          <Section title="Scrolling ticker" description="The moving strip under the hero.">
            <Field label="Phrases">
              <StringList rows={1} items={data.marquee.items ?? []} onChange={(v) => set('marquee', 'items', v)} placeholder="A short phrase" />
            </Field>
            <Field label="Speed (seconds per loop)" htmlFor="marquee-speed" hint="Higher is slower.">
              <Input id="marquee-speed" type="number" value={data.marquee.speed ?? 30} onChange={(e) => set('marquee', 'speed', Number(e.target.value) || 30)} />
            </Field>
          </Section>

          <Section title="Work & Services headings">
            <Field label="Work page title" htmlFor="work-title">
              <Input id="work-title" value={data.workIndex.title ?? ''} onChange={(e) => set('workIndex', 'title', e.target.value)} />
            </Field>
            <Field label="Work page lead" htmlFor="work-lead">
              <Textarea id="work-lead" rows={3} value={data.workIndex.lead ?? ''} onChange={(e) => set('workIndex', 'lead', e.target.value)} />
            </Field>
            <Field label="Home 'selected work' heading" htmlFor="work-home">
              <Input id="work-home" value={data.workIndex.homeHeading ?? ''} onChange={(e) => set('workIndex', 'homeHeading', e.target.value)} />
            </Field>
            <Field label="Services page title" htmlFor="svc-title">
              <Input id="svc-title" value={data.servicesIndex.title ?? ''} onChange={(e) => set('servicesIndex', 'title', e.target.value)} />
            </Field>
            <Field label="Home 'what we do' heading" htmlFor="svc-home">
              <Input id="svc-home" value={data.servicesIndex.homeHeading ?? ''} onChange={(e) => set('servicesIndex', 'homeHeading', e.target.value)} />
            </Field>
          </Section>

          <Section title="Footer">
            <Field label="Headline" htmlFor="footer-headline">
              <Input id="footer-headline" value={data.footer.headline ?? ''} onChange={(e) => set('footer', 'headline', e.target.value)} />
            </Field>
            <Field label="Sub-line" htmlFor="footer-sub">
              <Textarea id="footer-sub" rows={2} value={data.footer.sub ?? ''} onChange={(e) => set('footer', 'sub', e.target.value)} />
            </Field>
          </Section>
        </div>
      </div>
    </>
  );
}
